import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiGift, FiShoppingBag, FiCpu, FiCheck, FiMail, FiPhone, FiUser, FiChevronDown, FiShare2, FiCopy, FiArrowRight, FiStar, FiZap, FiClock } from 'react-icons/fi';
import api from '../api';

/* ───── Brand tokens ───── */
const C = {
  coral: '#E3425C', coralDark: '#D13A4F', coralLight: '#F8E0E3',
  charcoal: '#1C1C1E', charcoalMid: '#2C2C2E', charcoalSoft: '#3A3A3C',
  warmWhite: '#FAFAF9', gold: '#C9A84C', goldLight: '#E8D59A',
};

/* ───── Intersection Observer hook ───── */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ───── Scroll-reveal wrapper ───── */
function Reveal({ children, delay = 0, style = {} }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{ ...style, opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(32px)', transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s` }}>
      {children}
    </div>
  );
}

/* ───── Animated counter ───── */
function AnimatedCount({ end, duration = 1600 }) {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView();
  useEffect(() => {
    if (!inView || !end) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => { start += step; if (start >= end) { setCount(end); clearInterval(timer); } else setCount(Math.floor(start)); }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration]);
  return <span ref={ref}>{count}</span>;
}

/* ───── Confetti animation ───── */
function Confetti({ active }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const colors = [C.coral, C.gold, C.goldLight, C.coralLight, '#fff', '#FF6B8A', '#FFD700'];
    const particles = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * canvas.height * 0.5,
      w: 6 + Math.random() * 8,
      h: 4 + Math.random() * 6,
      color: colors[Math.floor(Math.random() * colors.length)],
      vx: (Math.random() - 0.5) * 4,
      vy: 2 + Math.random() * 4,
      rotation: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 10,
      opacity: 1,
    }));
    let frame;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      particles.forEach(p => {
        if (p.opacity <= 0) return;
        alive = true;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.06;
        p.rotation += p.rotSpeed;
        if (p.y > canvas.height + 20) { p.opacity = 0; return; }
        if (p.y > canvas.height * 0.7) p.opacity -= 0.015;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      if (alive) frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [active]);
  if (!active) return null;
  return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999 }} />;
}

/* ───── Floating particles background ───── */
function ParticlesBg() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let w, h;
    const resize = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    const dots = Array.from({ length: 40 }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      r: 1 + Math.random() * 2,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      color: Math.random() > 0.5 ? C.coral : C.gold,
      opacity: 0.15 + Math.random() * 0.25,
    }));
    let frame;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      dots.forEach(d => {
        d.x += d.vx; d.y += d.vy;
        if (d.x < 0 || d.x > w) d.vx *= -1;
        if (d.y < 0 || d.y > h) d.vy *= -1;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = d.color;
        ctx.globalAlpha = d.opacity;
        ctx.fill();
      });
      // Connect close dots
      ctx.globalAlpha = 0.06;
      ctx.strokeStyle = C.coral;
      ctx.lineWidth = 0.5;
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          if (dx * dx + dy * dy < 15000) {
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.stroke();
          }
        }
      }
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
}

/* ───── Event type options ───── */
const EVENT_TYPES = [
  { value: '', label: 'What event are you planning?' },
  { value: 'wedding', label: 'Wedding' },
  { value: 'lobola', label: 'Lobola' },
  { value: 'birthday', label: 'Birthday' },
  { value: 'baby_shower', label: 'Baby Shower' },
  { value: 'corporate', label: 'Corporate Event' },
  { value: 'funeral', label: 'Funeral' },
  { value: 'other', label: 'Other' },
];

/* ───── Feature cards data ───── */
const FEATURES = [
  { icon: FiCalendar, title: 'Free Event Pages', desc: 'Create beautiful event pages with RSVP tracking, guest management, and real-time updates.', color: C.coral },
  { icon: FiGift, title: 'Gift Registry', desc: 'Physical gifts and cash contributions in one place. Send money via EFT or mobile wallets.', color: C.gold },
  { icon: FiShoppingBag, title: 'Vendor Marketplace', desc: 'Connect with vetted caterers, photographers, DJs, and decor specialists near you.', color: C.coral },
  { icon: FiCpu, title: 'Smart Planning Tools', desc: 'Checklists, budgets, timelines, and AI assistance to keep everything on track.', color: C.gold },
];

/* ───── Perks data ───── */
const PERKS = [
  { icon: FiStar, text: 'First 100 sign-ups get LIFETIME Premium access', highlight: true },
  { icon: FiZap, text: 'Priority vendor matching in your area' },
  { icon: FiClock, text: 'Early access before public launch' },
];

const SHARE_URL = 'https://grandgatherregistry.co.za/join';
const SHARE_TEXT = "I just joined the Grand Gather waitlist \u2014 South Africa's smartest event planning app! Join me:";

/* ═════════════════════════════════════════════ */
/*               PRELAUNCH PAGE                  */
/* ═════════════════════════════════════════════ */
export default function PreLaunch() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', event_type: '' });
  const [submitted, setSubmitted] = useState(false);
  const [signupCount, setSignupCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const formRef = useRef(null);

  /* Fetch signup count */
  useEffect(() => {
    api.get('/early-signup/count')
      .then(r => setSignupCount(r.data.count || r.data || 0))
      .catch(() => {});
  }, []);

  /* Set page meta */
  useEffect(() => {
    document.title = 'Join Grand Gather — South Africa\'s Smartest Event Platform';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Be first to experience Grand Gather — the all-in-one platform for weddings, lobola, birthdays, and every celebration. Join the waitlist now!');
  }, []);

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) { setError('Please enter your email address.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { setError('Please enter a valid email address.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/early-signup', formData);
      setPosition(res.data.position || signupCount + 1);
      setSignupCount(prev => prev + 1);
      setSubmitted(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    } catch (err) {
      if (err.response?.status === 409) {
        setSubmitted(true);
        setPosition(err.response.data?.position || null);
        setError('already');
      } else {
        setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_TEXT + ' ' + SHARE_URL)}`, '_blank');
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(SHARE_URL).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  /* ──── Styles (CSS-in-JS for self-contained page) ──── */
  const s = {
    page: {
      minHeight: '100vh', background: C.warmWhite, fontFamily: "'DM Sans', sans-serif",
      color: C.charcoal, overflowX: 'hidden', WebkitFontSmoothing: 'antialiased',
    },
    /* ── Hero ── */
    hero: {
      position: 'relative', background: `linear-gradient(165deg, ${C.charcoal} 0%, ${C.charcoalMid} 50%, ${C.charcoalSoft} 100%)`,
      padding: '60px 20px 80px', textAlign: 'center', overflow: 'hidden', minHeight: '100vh',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    },
    heroGlow: {
      position: 'absolute', top: '-40%', left: '50%', transform: 'translateX(-50%)',
      width: '800px', height: '800px', borderRadius: '50%',
      background: `radial-gradient(circle, ${C.coral}18 0%, transparent 70%)`, pointerEvents: 'none',
    },
    heroGlowGold: {
      position: 'absolute', bottom: '-30%', right: '-10%',
      width: '600px', height: '600px', borderRadius: '50%',
      background: `radial-gradient(circle, ${C.gold}12 0%, transparent 70%)`, pointerEvents: 'none',
    },
    logo: { width: 64, height: 64, marginBottom: 16, filter: 'drop-shadow(0 4px 20px rgba(227,66,92,0.4))' },
    brand: {
      fontFamily: "'DM Serif Display', serif", fontSize: 20, color: '#fff', letterSpacing: 1,
      marginBottom: 24, opacity: 0.9,
    },
    badge: {
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '8px 20px', borderRadius: 100,
      background: `linear-gradient(135deg, ${C.coral}25, ${C.gold}20)`,
      border: `1px solid ${C.coral}40`, color: C.gold, fontSize: 13, fontWeight: 600,
      letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 28,
    },
    pulse: {
      width: 8, height: 8, borderRadius: '50%', background: C.coral,
      animation: 'prelaunch-pulse 2s ease-in-out infinite',
    },
    headline: {
      fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(28px, 7vw, 52px)',
      lineHeight: 1.15, color: '#fff', maxWidth: 700, margin: '0 auto 20px',
      textShadow: '0 2px 40px rgba(0,0,0,0.3)',
    },
    headlineAccent: { color: C.coral },
    subtitle: {
      fontSize: 'clamp(15px, 3.5vw, 18px)', color: 'rgba(255,255,255,0.7)',
      maxWidth: 520, margin: '0 auto 36px', lineHeight: 1.6,
    },
    ctaBtn: {
      display: 'inline-flex', alignItems: 'center', gap: 10,
      padding: '16px 36px', borderRadius: 14,
      background: `linear-gradient(135deg, ${C.coral}, ${C.coralDark})`,
      color: '#fff', fontSize: 17, fontWeight: 700, border: 'none', cursor: 'pointer',
      boxShadow: `0 8px 32px ${C.coral}50`, transition: 'all 0.3s',
      letterSpacing: 0.5,
    },
    countBadge: {
      marginTop: 28, display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '8px 16px', borderRadius: 100,
      background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontSize: 14,
    },
    countNum: { color: C.gold, fontWeight: 700 },

    /* ── Form section ── */
    formSection: {
      padding: '80px 20px', background: C.warmWhite, position: 'relative',
    },
    formContainer: {
      maxWidth: 520, margin: '0 auto',
      background: '#fff', borderRadius: 24, padding: 'clamp(28px, 5vw, 44px)',
      boxShadow: '0 20px 60px rgba(28,28,30,0.08), 0 1px 3px rgba(28,28,30,0.06)',
      border: `1px solid ${C.coralLight}`,
      position: 'relative', overflow: 'hidden',
    },
    formGlow: {
      position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)',
      width: '80%', height: 4,
      background: `linear-gradient(90deg, ${C.coral}, ${C.gold}, ${C.coral})`,
      borderRadius: '0 0 4px 4px',
    },
    formTitle: {
      fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(22px, 5vw, 28px)',
      textAlign: 'center', marginBottom: 6, color: C.charcoal,
    },
    formSubtitle: {
      textAlign: 'center', color: '#666', fontSize: 14, marginBottom: 28, lineHeight: 1.5,
    },
    inputGroup: { marginBottom: 16, position: 'relative' },
    inputIcon: {
      position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
      color: '#999', pointerEvents: 'none', fontSize: 18,
    },
    input: {
      width: '100%', padding: '15px 16px 15px 46px', borderRadius: 12,
      border: `1.5px solid #E8E8E8`, fontSize: 15, fontFamily: "'DM Sans', sans-serif",
      background: '#FAFAFA', transition: 'all 0.25s', outline: 'none', color: C.charcoal,
      boxSizing: 'border-box',
    },
    select: {
      width: '100%', padding: '15px 40px 15px 46px', borderRadius: 12,
      border: `1.5px solid #E8E8E8`, fontSize: 15, fontFamily: "'DM Sans', sans-serif",
      background: '#FAFAFA', transition: 'all 0.25s', outline: 'none', color: C.charcoal,
      appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer', boxSizing: 'border-box',
    },
    selectArrow: {
      position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
      color: '#999', pointerEvents: 'none',
    },
    submitBtn: {
      width: '100%', padding: '16px', borderRadius: 14, border: 'none',
      background: `linear-gradient(135deg, ${C.coral}, ${C.coralDark})`,
      color: '#fff', fontSize: 17, fontWeight: 700, cursor: 'pointer',
      boxShadow: `0 6px 24px ${C.coral}40`, transition: 'all 0.3s',
      marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      letterSpacing: 0.3,
    },
    submitBtnDisabled: { opacity: 0.7, cursor: 'not-allowed' },
    errorMsg: {
      background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 10,
      fontSize: 13, marginBottom: 12, textAlign: 'center', border: '1px solid #FECACA',
    },
    optional: { fontSize: 11, color: '#aaa', marginLeft: 4 },

    /* ── Success state ── */
    successBox: {
      textAlign: 'center', padding: '20px 0',
    },
    successEmoji: { fontSize: 56, marginBottom: 12 },
    successTitle: {
      fontFamily: "'DM Serif Display', serif", fontSize: 28, marginBottom: 8, color: C.charcoal,
    },
    successSub: { color: '#666', fontSize: 15, marginBottom: 6, lineHeight: 1.5 },
    positionBadge: {
      display: 'inline-block', padding: '6px 18px', borderRadius: 100,
      background: `linear-gradient(135deg, ${C.gold}20, ${C.coral}15)`,
      color: C.gold, fontWeight: 700, fontSize: 14, marginBottom: 24,
      border: `1px solid ${C.gold}30`,
    },
    shareSection: {
      borderTop: `1px solid #eee`, paddingTop: 24, marginTop: 8,
    },
    shareTitle: { fontSize: 15, fontWeight: 600, marginBottom: 14, color: C.charcoal },
    shareButtons: { display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' },
    whatsappBtn: {
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '12px 24px', borderRadius: 12,
      background: '#25D366', color: '#fff', fontSize: 14, fontWeight: 600,
      border: 'none', cursor: 'pointer', transition: 'all 0.2s',
    },
    copyBtn: {
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '12px 24px', borderRadius: 12,
      background: '#F3F4F6', color: C.charcoal, fontSize: 14, fontWeight: 600,
      border: '1px solid #E5E7EB', cursor: 'pointer', transition: 'all 0.2s',
    },

    /* ── Features section ── */
    featuresSection: {
      padding: '80px 20px', background: '#fff',
    },
    sectionTitle: {
      fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(24px, 5vw, 36px)',
      textAlign: 'center', marginBottom: 12, color: C.charcoal,
    },
    sectionSub: {
      textAlign: 'center', color: '#777', fontSize: 15, maxWidth: 500,
      margin: '0 auto 48px', lineHeight: 1.6,
    },
    featuresGrid: {
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: 20, maxWidth: 900, margin: '0 auto',
    },
    featureCard: {
      padding: 28, borderRadius: 20, background: C.warmWhite,
      border: '1px solid #F0EDE8', transition: 'all 0.35s',
    },
    featureIcon: {
      width: 52, height: 52, borderRadius: 14, display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 16,
    },
    featureTitle: { fontFamily: "'DM Serif Display', serif", fontSize: 18, marginBottom: 8, color: C.charcoal },
    featureDesc: { color: '#777', fontSize: 14, lineHeight: 1.6 },

    /* ── Perks section ── */
    perksSection: {
      padding: '80px 20px',
      background: `linear-gradient(165deg, ${C.charcoal} 0%, ${C.charcoalMid} 100%)`,
      position: 'relative', overflow: 'hidden',
    },
    perksList: { maxWidth: 560, margin: '0 auto' },
    perkItem: {
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '20px 24px', borderRadius: 16,
      background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.08)', marginBottom: 12,
      transition: 'all 0.3s',
    },
    perkItemHighlight: {
      background: `linear-gradient(135deg, ${C.coral}18, ${C.gold}12)`,
      border: `1px solid ${C.coral}30`,
    },
    perkIcon: {
      width: 42, height: 42, borderRadius: 12, display: 'flex',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    perkText: { color: 'rgba(255,255,255,0.9)', fontSize: 15, fontWeight: 500, lineHeight: 1.4 },
    perkHighlightText: { color: '#fff', fontWeight: 700 },

    /* ── Social proof ── */
    proofSection: {
      padding: '60px 20px', background: C.warmWhite, textAlign: 'center',
    },
    proofCount: {
      fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(40px, 10vw, 64px)',
      background: `linear-gradient(135deg, ${C.coral}, ${C.gold})`,
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      backgroundClip: 'text', marginBottom: 8,
    },
    proofLabel: { color: '#777', fontSize: 16 },
    proofCta: {
      marginTop: 24, display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '14px 32px', borderRadius: 14,
      background: `linear-gradient(135deg, ${C.coral}, ${C.coralDark})`,
      color: '#fff', fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer',
      boxShadow: `0 6px 24px ${C.coral}40`, transition: 'all 0.3s',
      textDecoration: 'none',
    },

    /* ── Footer ── */
    footer: {
      padding: '48px 20px 32px', background: C.charcoal, textAlign: 'center',
    },
    footerLinks: {
      display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px 24px', marginBottom: 20,
    },
    footerLink: {
      color: 'rgba(255,255,255,0.6)', fontSize: 14, textDecoration: 'none',
      display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'color 0.2s',
    },
    footerCopy: { color: 'rgba(255,255,255,0.3)', fontSize: 12, marginTop: 20 },
  };

  return (
    <div style={s.page}>
      <Confetti active={showConfetti} />

      {/* ── Keyframe animation injection ── */}
      <style>{`
        @keyframes prelaunch-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.6); }
        }
        @keyframes prelaunch-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes prelaunch-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes prelaunch-check {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        .prelaunch-input:focus {
          border-color: ${C.coral} !important;
          background: #fff !important;
          box-shadow: 0 0 0 4px ${C.coral}15 !important;
        }
        .prelaunch-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 36px ${C.coral}55 !important;
        }
        .prelaunch-btn:active {
          transform: translateY(0);
        }
        .prelaunch-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(28,28,30,0.08);
          border-color: ${C.coral}30 !important;
        }
        .prelaunch-perk:hover {
          background: rgba(255,255,255,0.1) !important;
          transform: translateX(4px);
        }
        .prelaunch-wa-btn:hover { background: #22c55e !important; transform: translateY(-1px); }
        .prelaunch-copy-btn:hover { background: #E8E8E8 !important; }
        .prelaunch-footer-link:hover { color: ${C.coral} !important; }
      `}</style>

      {/* ═══════════════ HERO ═══════════════ */}
      <section style={s.hero}>
        <div style={s.heroGlow} />
        <div style={s.heroGlowGold} />
        <ParticlesBg />

        <Reveal>
          <img src="/gift-icon-v2.png" alt="Grand Gather" style={s.logo} />
        </Reveal>

        <Reveal delay={0.1}>
          <div style={s.brand}>GRAND GATHER</div>
        </Reveal>

        <Reveal delay={0.15}>
          <div style={s.badge}>
            <div style={s.pulse} />
            Launching Soon
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <h1 style={s.headline}>
            Be First to Experience South Africa's{' '}
            <span style={s.headlineAccent}>Smartest Event Platform</span>
          </h1>
        </Reveal>

        <Reveal delay={0.3}>
          <p style={s.subtitle}>
            Plan weddings, lobola, birthdays, and every celebration with one powerful app.
            Gift registries, vendor matching, RSVPs — all in one place.
          </p>
        </Reveal>

        <Reveal delay={0.4}>
          <button style={s.ctaBtn} className="prelaunch-btn" onClick={scrollToForm}>
            Join the Waitlist <FiArrowRight />
          </button>
        </Reveal>

        {signupCount > 0 && (
          <Reveal delay={0.5}>
            <div style={s.countBadge}>
              <span style={s.countNum}>{signupCount}</span> people already joined
            </div>
          </Reveal>
        )}
      </section>

      {/* ═══════════════ FORM SECTION ═══════════════ */}
      <section style={s.formSection} ref={formRef} id="signup-form">
        <Reveal>
          <div style={s.formContainer}>
            <div style={s.formGlow} />

            {!submitted ? (
              <>
                <h2 style={s.formTitle}>Reserve Your Spot</h2>
                <p style={s.formSubtitle}>
                  Join the waitlist and be among the first to access Grand Gather when we launch.
                </p>

                {error && error !== 'already' && <div style={s.errorMsg}>{error}</div>}

                <form onSubmit={handleSubmit}>
                  <div style={s.inputGroup}>
                    <FiUser style={s.inputIcon} />
                    <input
                      className="prelaunch-input"
                      style={s.input}
                      type="text"
                      name="name"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete="name"
                    />
                    <span style={{ ...s.optional, position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}>Optional</span>
                  </div>

                  <div style={s.inputGroup}>
                    <FiMail style={s.inputIcon} />
                    <input
                      className="prelaunch-input"
                      style={s.input}
                      type="email"
                      name="email"
                      placeholder="Email address *"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div style={s.inputGroup}>
                    <FiPhone style={s.inputIcon} />
                    <input
                      className="prelaunch-input"
                      style={s.input}
                      type="tel"
                      name="phone"
                      placeholder="WhatsApp number (e.g. 0612345678)"
                      value={formData.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                    />
                    <span style={{ ...s.optional, position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}>Optional</span>
                  </div>

                  <div style={s.inputGroup}>
                    <FiCalendar style={s.inputIcon} />
                    <select
                      className="prelaunch-input"
                      style={{ ...s.select, color: formData.event_type ? C.charcoal : '#999' }}
                      name="event_type"
                      value={formData.event_type}
                      onChange={handleChange}
                    >
                      {EVENT_TYPES.map(opt => (
                        <option key={opt.value} value={opt.value} disabled={opt.value === ''}>{opt.label}</option>
                      ))}
                    </select>
                    <FiChevronDown style={s.selectArrow} />
                  </div>

                  <button
                    type="submit"
                    style={{ ...s.submitBtn, ...(loading ? s.submitBtnDisabled : {}) }}
                    className="prelaunch-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span style={{ width: 20, height: 20, border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'prelaunch-pulse 0.8s linear infinite', display: 'inline-block' }} />
                        Joining...
                      </>
                    ) : (
                      <>Join the Waitlist <FiArrowRight /></>
                    )}
                  </button>
                </form>

                <p style={{ textAlign: 'center', fontSize: 12, color: '#aaa', marginTop: 14 }}>
                  No spam, ever. We'll only email you about the launch.
                </p>
              </>
            ) : (
              /* ── Success state ── */
              <div style={s.successBox}>
                <div style={s.successEmoji}>
                  {error === 'already' ? '\uD83D\uDC4B' : '\uD83C\uDF89'}
                </div>
                <h2 style={s.successTitle}>
                  {error === 'already' ? "You're Already on the List!" : "You're In!"}
                </h2>
                <p style={s.successSub}>
                  {error === 'already'
                    ? "Great news — you've already secured your spot. We'll be in touch soon!"
                    : "Welcome to the Grand Gather family! We'll notify you the moment we launch."
                  }
                </p>
                {position && error !== 'already' && (
                  <div style={s.positionBadge}>
                    You're #{position} on the waitlist
                  </div>
                )}

                <div style={s.shareSection}>
                  <p style={s.shareTitle}>Share with friends & help them get early access too</p>
                  <div style={s.shareButtons}>
                    <button className="prelaunch-wa-btn" style={s.whatsappBtn} onClick={shareWhatsApp}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Share on WhatsApp
                    </button>
                    <button className="prelaunch-copy-btn" style={s.copyBtn} onClick={copyLink}>
                      {linkCopied ? <><FiCheck /> Copied!</> : <><FiCopy /> Copy Link</>}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </section>

      {/* ═══════════════ FEATURES ═══════════════ */}
      <section style={s.featuresSection}>
        <Reveal>
          <h2 style={s.sectionTitle}>Everything You Need to Celebrate</h2>
          <p style={s.sectionSub}>
            Grand Gather brings all your event planning tools into one beautiful platform, built for South Africa.
          </p>
        </Reveal>

        <div style={s.featuresGrid}>
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.1}>
              <div style={s.featureCard} className="prelaunch-card">
                <div style={{ ...s.featureIcon, background: `${f.color}15`, color: f.color }}>
                  <f.icon size={24} />
                </div>
                <h3 style={s.featureTitle}>{f.title}</h3>
                <p style={s.featureDesc}>{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════ EARLY BIRD PERKS ═══════════════ */}
      <section style={s.perksSection}>
        <Reveal>
          <h2 style={{ ...s.sectionTitle, color: '#fff', marginBottom: 8 }}>Early Bird Perks</h2>
          <p style={{ ...s.sectionSub, color: 'rgba(255,255,255,0.5)', marginBottom: 36 }}>
            Sign up now and unlock exclusive benefits
          </p>
        </Reveal>

        <div style={s.perksList}>
          {PERKS.map((p, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div
                className="prelaunch-perk"
                style={{ ...s.perkItem, ...(p.highlight ? s.perkItemHighlight : {}) }}
              >
                <div style={{ ...s.perkIcon, background: p.highlight ? `${C.gold}25` : 'rgba(255,255,255,0.08)', color: p.highlight ? C.gold : 'rgba(255,255,255,0.6)' }}>
                  <p.icon size={20} />
                </div>
                <span style={{ ...s.perkText, ...(p.highlight ? s.perkHighlightText : {}) }}>
                  {p.text}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════ SOCIAL PROOF ═══════════════ */}
      <section style={s.proofSection}>
        <Reveal>
          <div style={s.proofCount}>
            <AnimatedCount end={signupCount} />+
          </div>
          <p style={s.proofLabel}>
            {signupCount > 0
              ? `Join ${signupCount} others already on the waitlist`
              : 'Be the first to join the waitlist'
            }
          </p>
          {!submitted && (
            <button style={s.proofCta} className="prelaunch-btn" onClick={scrollToForm}>
              Claim Your Spot <FiArrowRight />
            </button>
          )}
        </Reveal>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer style={s.footer}>
        <div style={s.footerLinks}>
          <a href="https://wa.me/27613911942" target="_blank" rel="noopener noreferrer" style={s.footerLink} className="prelaunch-footer-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp Us
          </a>
          <a href="mailto:info@grandgatherregistry.co.za" style={s.footerLink} className="prelaunch-footer-link">
            <FiMail size={14} /> info@grandgatherregistry.co.za
          </a>
          <Link to="/" style={s.footerLink} className="prelaunch-footer-link">
            Visit Grand Gather
          </Link>
        </div>

        <div style={s.shareButtons}>
          <button className="prelaunch-wa-btn" style={{ ...s.whatsappBtn, fontSize: 13, padding: '10px 20px' }} onClick={shareWhatsApp}>
            <FiShare2 size={14} /> Share on WhatsApp
          </button>
          <button className="prelaunch-copy-btn" style={{ ...s.copyBtn, fontSize: 13, padding: '10px 20px' }} onClick={copyLink}>
            {linkCopied ? <><FiCheck size={14} /> Copied!</> : <><FiCopy size={14} /> Copy Link</>}
          </button>
        </div>

        <p style={s.footerCopy}>
          &copy; 2025–{new Date().getFullYear()} Grand Gather Registry (Pty) Ltd. All rights reserved. Made with love in South Africa.
        </p>
      </footer>
    </div>
  );
}
